import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api';
import { useToast } from '../components/Toast';
import RegionSelect from '../components/RegionSelect';
import { COUNTRY_ISO, SIZES, PROCUREMENT_TYPE_OPTIONS, CONTRACT_SIZE_OPTIONS } from '../constants';
import { CPV_LABELS } from '../data/cpv';
import { ISO3_TO_CC } from '../data/nuts';
import { useCompany, useUsage, QK } from '../queries';

// ── Constants ─────────────────────────────────────────────────────────────────

const BUSINESS_TYPES = [
  'IT & Software',
  'Consulting & Advisory',
  'Construction & Infrastructure',
  'Healthcare & Medical',
  'Research & Development',
  'Energy & Environment',
  'Transport & Logistics',
  'Education & Training',
  'Defence & Security',
  'Finance & Accounting',
  'Marketing & Communications',
  'Legal Services',
  'Other',
];

const SERVICES_BY_TYPE = {
  'IT & Software': [
    'Custom software development', 'Cloud & DevOps infrastructure', 'Cybersecurity',
    'Data & AI systems', 'System integration', 'IT maintenance & support',
    'Digital transformation consulting',
  ],
  'Consulting & Advisory': [
    'Strategy consulting', 'IT consulting', 'Financial advisory',
    'HR & organisational development', 'Policy & regulatory advisory',
    'Project management', 'Change management',
  ],
  'Construction & Infrastructure': [
    'Building construction', 'Civil & structural engineering', 'Site preparation',
    'Building installation (HVAC, electrical)', 'Renovation & restoration',
    'Infrastructure design', 'Project supervision',
  ],
  'Healthcare & Medical': [
    'Medical equipment supply', 'Healthcare IT systems', 'Patient care services',
    'Laboratory services', 'Health training & education', 'Pharmaceutical supply',
  ],
  'Research & Development': [
    'Applied research & studies', 'Technology development',
    'Evaluation & impact assessment', 'Innovation projects', 'Scientific consulting',
  ],
  'Energy & Environment': [
    'Renewable energy solutions', 'Energy efficiency consulting',
    'Environmental impact assessment', 'Waste management', 'Water & sewage services',
    'Sustainability consulting',
  ],
  'Transport & Logistics': [
    'Freight transport', 'Urban & public transport', 'Fleet management',
    'Logistics & warehousing', 'Traffic management systems',
  ],
  'Education & Training': [
    'Professional skills training', 'E-learning & digital education',
    'Vocational training', 'Safety & compliance training', 'Curriculum development',
  ],
  'Defence & Security': [
    'Physical security services', 'Cybersecurity & IT security',
    'Investigation services', 'Security equipment supply', 'Emergency management',
  ],
  'Finance & Accounting': [
    'Accounting & audit', 'Financial consulting', 'Insurance services',
    'Tax advisory', 'Budget & financial management',
  ],
  'Marketing & Communications': [
    'Communication campaigns', 'Digital marketing & SEO',
    'Translation & interpretation', 'PR & media relations', 'Event management',
  ],
  'Legal Services': [
    'Legal consulting', 'Regulatory compliance', 'Contract management',
    'Litigation support', 'Intellectual property',
  ],
  'Other': [
    'Facilities management', 'Catering & hospitality',
    'Human resources', 'Printing & publications', 'Other services',
  ],
};

const COUNTRY_NAME_TO_CODE = Object.fromEntries(COUNTRY_ISO.map((c) => [c.name, c.code]));
const COUNTRY_CODE_TO_NAME = Object.fromEntries(COUNTRY_ISO.map((c) => [c.code, c.name]));

// ── Styles ────────────────────────────────────────────────────────────────────

const s = {
  page: {
    maxWidth: 680, margin: '0 auto', padding: '48px 24px 80px',
  },
  progressWrap: {
    display: 'flex', alignItems: 'center', gap: 12, marginBottom: 40,
  },
  progressBar: {
    flex: 1, height: 4, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden',
  },
  progressFill: (pct) => ({
    height: '100%', width: pct + '%',
    background: 'linear-gradient(90deg, #1e40af, #3b82f6)',
    borderRadius: 4, transition: 'width 0.35s ease',
  }),
  progressLabel: { fontSize: 13, color: '#94a3b8', whiteSpace: 'nowrap' },
  heading: { fontSize: 26, fontWeight: 700, color: '#0f172a', marginBottom: 8 },
  subheading: { fontSize: 15, color: '#64748b', marginBottom: 32, lineHeight: 1.6 },

  // Start screen cards
  pathGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 32 },
  pathGridMobile: { display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 32 },
  pathCard: (active) => ({
    border: active ? '2px solid #1e40af' : '1px solid #e2e8f0',
    borderRadius: 12, padding: '24px 20px', cursor: 'pointer',
    background: active ? '#eff6ff' : '#fff',
    transition: 'all 0.15s',
  }),
  pathIcon: { fontSize: 28, marginBottom: 12 },
  pathTitle: { fontSize: 15, fontWeight: 600, color: '#0f172a', marginBottom: 6 },
  pathDesc: { fontSize: 13, color: '#64748b', lineHeight: 1.5 },

  // URL input
  urlInput: {
    width: '100%', padding: '12px 16px', border: '1px solid #d1d5db',
    borderRadius: 8, fontSize: 15, color: '#1e293b', outline: 'none', boxSizing: 'border-box',
  },
  urlInputError: {
    width: '100%', padding: '12px 16px', border: '1px solid #f87171',
    borderRadius: 8, fontSize: 15, color: '#1e293b', outline: 'none', boxSizing: 'border-box',
  },

  // Question screens
  questionLabel: { fontSize: 18, fontWeight: 600, color: '#0f172a', marginBottom: 6 },
  questionHint: { fontSize: 14, color: '#64748b', marginBottom: 24 },

  // Big option chips (single/multi choice)
  optionGrid: { display: 'flex', flexWrap: 'wrap', gap: 10 },
  optionChip: (selected) => ({
    padding: '9px 18px', borderRadius: 24, fontSize: 14, cursor: 'pointer',
    border: selected ? '2px solid #1e40af' : '1px solid #d1d5db',
    background: selected ? '#eff6ff' : '#fff',
    color: selected ? '#1e40af' : '#374151',
    fontWeight: selected ? 600 : 400,
    transition: 'all 0.12s',
  }),

  // Form fields
  fieldWrap: { marginBottom: 20 },
  fieldLabel: { display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6 },
  fieldHint: { fontSize: 12, color: '#94a3b8', marginTop: 4 },
  input: {
    width: '100%', padding: '10px 14px', border: '1px solid #d1d5db',
    borderRadius: 8, fontSize: 14, color: '#1e293b', outline: 'none', boxSizing: 'border-box',
  },
  inputError: {
    width: '100%', padding: '10px 14px', border: '1px solid #f87171',
    borderRadius: 8, fontSize: 14, color: '#1e293b', outline: 'none', boxSizing: 'border-box',
  },
  select: {
    width: '100%', padding: '10px 14px', border: '1px solid #d1d5db',
    borderRadius: 8, fontSize: 14, color: '#1e293b', outline: 'none',
    background: '#fff', boxSizing: 'border-box',
  },
  textarea: {
    width: '100%', padding: '12px 14px', border: '1px solid #d1d5db',
    borderRadius: 8, fontSize: 14, color: '#1e293b', outline: 'none',
    minHeight: 120, resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6,
  },
  fieldError: { fontSize: 12, color: '#b91c1c', marginTop: 4 },

  // Review chips
  reviewSection: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
    padding: '20px 24px', marginBottom: 16,
  },
  reviewSectionTitle: {
    fontSize: 13, fontWeight: 600, color: '#64748b', textTransform: 'uppercase',
    letterSpacing: '0.05em', marginBottom: 14,
  },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  chip: {
    background: '#eff6ff', color: '#1d4ed8', fontSize: 13,
    padding: '4px 12px', borderRadius: 20,
    display: 'inline-flex', alignItems: 'center', gap: 6,
  },
  chipGray: {
    background: '#f1f5f9', color: '#475569', fontSize: 13,
    padding: '4px 12px', borderRadius: 20,
    display: 'inline-flex', alignItems: 'center', gap: 6,
  },
  chipRemove: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#93c5fd', fontWeight: 700, fontSize: 14, padding: 0, lineHeight: 1,
  },
  chipRemoveGray: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#94a3b8', fontWeight: 700, fontSize: 14, padding: 0, lineHeight: 1,
  },
  emptyHint: { fontSize: 13, color: '#94a3b8', fontStyle: 'italic' },
  capNotice: {
    background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e',
    borderRadius: 8, padding: '12px 16px', fontSize: 14, marginTop: 16,
  },
  capUpgrade: {
    background: 'none', border: 'none', padding: 0, color: '#92400e',
    fontWeight: 700, cursor: 'pointer', textDecoration: 'underline',
  },
  laterHint: { fontSize: 13, color: '#64748b', marginTop: 14, lineHeight: 1.5 },

  // Buttons
  btnRow: { display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 32 },
  btnPrimary: (disabled) => ({
    background: disabled ? '#93c5fd' : '#1e40af', color: '#fff',
    border: 'none', fontWeight: 600, fontSize: 15, padding: '12px 28px',
    borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer',
  }),
  btnSecondary: {
    background: '#fff', color: '#374151', border: '1px solid #d1d5db',
    fontWeight: 500, fontSize: 15, padding: '12px 22px',
    borderRadius: 8, cursor: 'pointer',
  },
  btnGhost: {
    background: 'none', border: 'none', color: '#94a3b8',
    fontSize: 14, cursor: 'pointer', padding: '12px 0',
  },

  // Misc
  errorBox: {
    background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
    borderRadius: 8, padding: '12px 16px', fontSize: 14, marginBottom: 20,
  },
  confidenceBadge: (conf) => ({
    display: 'inline-block',
    padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600,
    background: conf >= 0.7 ? '#dcfce7' : conf >= 0.4 ? '#fef9c3' : '#fee2e2',
    color: conf >= 0.7 ? '#16a34a' : conf >= 0.4 ? '#a16207' : '#dc2626',
    marginBottom: 20,
  }),
  spinnerWrap: { textAlign: 'center', padding: '60px 0' },
  spinner: {
    width: 40, height: 40, borderRadius: '50%',
    border: '3px solid #e2e8f0', borderTopColor: '#1e40af',
    animation: 'tm-spin 0.7s linear infinite', margin: '0 auto 20px',
  },
  spinnerLabel: { fontSize: 15, color: '#64748b' },
  addChipInput: {
    padding: '4px 10px', border: '1px dashed #d1d5db', borderRadius: 20,
    fontSize: 13, color: '#374151', outline: 'none', minWidth: 120,
  },
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function Spinner({ label }) {
  return (
    <div style={s.spinnerWrap}>
      <style>{`@keyframes tm-spin { to { transform: rotate(360deg); } }`}</style>
      <div style={s.spinner} />
      <div style={s.spinnerLabel}>{label}</div>
    </div>
  );
}

function ProgressBar({ current, total }) {
  return (
    <div style={s.progressWrap}>
      <div style={s.progressBar}>
        <div style={s.progressFill(Math.round((current / total) * 100))} />
      </div>
      <div style={s.progressLabel}>Step {current} of {total}</div>
    </div>
  );
}

function ChipList({ items, onRemove, labels, gray }) {
  const chipStyle = gray ? s.chipGray : s.chip;
  const removeStyle = gray ? s.chipRemoveGray : s.chipRemove;
  if (!items || items.length === 0) return <span style={s.emptyHint}>None selected</span>;
  return (
    <div style={s.chipRow}>
      {items.map((item) => (
        <span key={item} style={chipStyle}>
          {labels ? (labels[item] || item) : item}
          <button style={removeStyle} type="button" onClick={() => onRemove(item)}>×</button>
        </span>
      ))}
    </div>
  );
}

function AddChipInput({ onAdd, placeholder }) {
  const [val, setVal] = useState('');
  function commit() {
    const v = val.trim();
    if (v) { onAdd(v); setVal(''); }
  }
  return (
    <input
      style={s.addChipInput}
      value={val}
      placeholder={placeholder || '+ Add'}
      onChange={(e) => setVal(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(); } }}
      onBlur={commit}
    />
  );
}

// Country + region picker, shared by the geography step and the multi-profile
// review so the two can't drift. `value` is {countries, regions}; `max` is the
// tier country cap (null = unlimited, in which case empty legitimately means
// "all EU" — on a capped tier the backend rejects empty at create time).
function GeographyPicker({ value, onChange, max, onUpgrade }) {
  const capped = typeof max === 'number';
  const atCap = capped && value.countries.length >= max;

  function toggleCountry(code) {
    const on = value.countries.includes(code);
    if (!on && atCap) return;
    const countries = on ? value.countries.filter((c) => c !== code) : [...value.countries, code];
    // Drop region picks for a country that's no longer selected — mirrors
    // prune_regions_to_countries() server-side so a stale pick can't gate matches.
    const allowed = new Set(countries.map((c) => ISO3_TO_CC[c]).filter(Boolean));
    onChange({ countries, regions: value.regions.filter((r) => allowed.has(String(r).slice(0, 2))) });
  }

  return (
    <>
      <div style={s.optionGrid}>
        {COUNTRY_ISO.map((c) => {
          const selected = value.countries.includes(c.code);
          const locked = !selected && atCap;
          return (
            <button
              key={c.code}
              type="button"
              disabled={locked}
              style={{
                ...s.optionChip(selected),
                opacity: locked ? 0.4 : 1,
                cursor: locked ? 'not-allowed' : 'pointer',
              }}
              onClick={() => toggleCountry(c.code)}
            >
              {c.name}
            </button>
          );
        })}
      </div>

      {atCap && (
        <div style={s.capNotice}>
          Your plan covers {max} {max === 1 ? 'country' : 'countries'}.{' '}
          <button style={s.capUpgrade} onClick={onUpgrade}>Upgrade</button>{' '}
          to find tenders across the EU.
        </div>
      )}

      <div style={{ marginTop: 24 }}>
        <div style={s.fieldLabel}>
          Regions <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span>
        </div>
        <div style={{ ...s.questionHint, marginBottom: 12 }}>
          Leave a country whole if you deliver nationwide.
        </div>
        <RegionSelect
          operatingCountries={value.countries}
          value={value.regions}
          onChange={(regions) => onChange({ ...value, regions })}
        />
      </div>
    </>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function Onboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const showToast = useToast();
  const queryClient = useQueryClient();

  const source = new URLSearchParams(location.search).get('source'); // 'profile' | null

  const [step, setStep] = useState('start');
  // Shared /company read — drives whether the wizard asks for company details.
  // null while loading, then true/false. (404 = no company yet, hence retry:false.)
  const companyQuery = useCompany({ retry: false });
  const existingCompany = companyQuery.data ?? null;
  const companyExists = companyQuery.isLoading ? null : !!companyQuery.data;

  // Tier profile cap — drives how many product lines the customer may build.
  // remainingCap = what's left after any profiles they already have.
  const usageQuery = useUsage({ retry: false });
  const profilesMax = usageQuery.data?.profiles_max;
  const profilesActive = usageQuery.data?.profiles_active || 0;
  const remainingCap = profilesMax == null ? Infinity : Math.max(0, profilesMax - profilesActive);
  const planLabel = usageQuery.data?.plan || 'your';
  // Tier country cap — null/undefined = all EU. When it IS set, the backend now
  // rejects a profile created with no countries at all (an empty list means "all
  // EU", which a capped plan isn't paying for), so the picker must require one.
  const countriesMax = usageQuery.data?.countries_max ?? null;
  const countryPickRequired = typeof countriesMax === 'number';

  // Website scan state
  const [scanUrl, setScanUrl] = useState('');
  const [scanError, setScanError] = useState('');

  // Questionnaire answers
  const [answers, setAnswers] = useState({
    company_name: '',
    country: '',
    company_size: '',
    business_type: '',
    services: [],
    countries: [],
    regions: [],
    description: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});

  // Draft from AI (single/balanced path — the existing rich review renders this)
  const [draft, setDraft] = useState(null);

  // Two-phase multi-profile state (website scan only)
  const [scan, setScan] = useState(null);            // Phase-1 result incl. website_text
  const [selectedLineKeys, setSelectedLineKeys] = useState([]);
  const [multiDrafts, setMultiDrafts] = useState(null); // split-mode built drafts (array)
  const [buildStatus, setBuildStatus] = useState('');   // progress label for multi create

  // Geography — asked ONCE on its own step and applied to every profile in the
  // batch (it's a company-level fact, and per-profile divergence is a refinement
  // for someone who has seen their matches). Seeded from the scanned company
  // country, which the customer confirms rather than inherits silently.
  const [geo, setGeo] = useState({ countries: [], regions: [] });
  // What to build once geography is answered: {mode, scan}. Carries the scan
  // result explicitly because setScan() hasn't committed yet on the first hop.
  const [pendingBuild, setPendingBuild] = useState(null);

  // Save state
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [mode, setMode] = useState('form'); // 'form' | 'created' | 'finding'
  const [savedProfileId, setSavedProfileId] = useState(null);
  const [savedProfileName, setSavedProfileName] = useState('');
  const [matchProgress, setMatchProgress] = useState(0);

  function setAnswer(field, value) {
    setAnswers((a) => ({ ...a, [field]: value }));
    if (fieldErrors[field]) setFieldErrors((e) => ({ ...e, [field]: null }));
  }

  // ── Website scan path ──────────────────────────────────────────────────────

  // Phase 1: cheap scan → identify distinct product lines. If the split is
  // obvious, ask the customer which lines to build; otherwise build the single
  // blended profile straight away (today's flow).
  async function handleScan() {
    setScanError('');
    if (!scanUrl.trim()) { setScanError('Please enter a website URL'); return; }
    setStep('scanning');
    try {
      const result = await apiFetch('/profiles/onboarding/scan-website', {
        method: 'POST',
        body: JSON.stringify({ url: scanUrl }),
        timeoutMs: 90000, // deep multi-page crawl + LLM can exceed the 30s default
      });
      // Pre-fill company fields from existing company if it exists
      if (companyExists && existingCompany) {
        result.company = {
          company_name: existingCompany.company_name,
          country: existingCompany.country,
          company_size: existingCompany.company_size,
        };
      }
      setScan(result);
      // Seed the geography answer from the company country we just read (or the
      // one already on file). A derived default the customer confirms — never a
      // silent auto-value, and never overwriting picks they already made.
      const seedCountry = result.company?.country;
      setGeo((g) => (g.countries.length > 0 || !seedCountry || !COUNTRY_CODE_TO_NAME[seedCountry]
        ? g
        : { countries: [seedCountry], regions: [] }));
      const lines = result.product_lines || [];
      if (result.multi && lines.length >= 2) {
        // Pre-select up to the tier cap so the primary CTA is immediately usable.
        const limit = remainingCap === Infinity ? lines.length : remainingCap;
        setSelectedLineKeys(lines.slice(0, limit).map((l) => l.key));
        setStep('choose_lines');
      } else {
        goToGeography('balanced', result);
      }
    } catch (err) {
      setScanError(err.message || 'Could not analyze website. Try the questionnaire instead.');
      setStep('scan_url');
    }
  }

  // Between choosing lines and building: ask where they deliver. One answer for
  // the whole batch — see local-docs/onboarding_geography_plan.md.
  function goToGeography(buildMode, scanResult) {
    setPendingBuild({ mode: buildMode, scan: scanResult || scan });
    setStep('geography');
  }

  // Phase 2: build the full draft(s) for the customer's selection. `balanced`
  // (or a single picked line) → the existing rich review; ≥2 lines → multi review.
  async function buildProfiles(buildMode, scanResult) {
    const sc = scanResult || scan;
    setStep('building');
    try {
      const lines = buildMode === 'split'
        ? (sc.product_lines || []).filter((l) => selectedLineKeys.includes(l.key))
        : [];
      const result = await apiFetch('/profiles/onboarding/build-profiles', {
        method: 'POST',
        body: JSON.stringify({
          website_text: sc.website_text,
          mode: buildMode,
          lines,
          operating_countries: geo.countries,
          operating_regions: geo.regions,
        }),
        timeoutMs: 120000, // one big LLM call + N CPV lookups
      });
      const drafts = result.drafts || [];
      if (drafts.length === 0) throw new Error('No profiles were built.');
      const company = (companyExists && existingCompany)
        ? { company_name: existingCompany.company_name, country: existingCompany.country, company_size: existingCompany.company_size }
        : sc.company;
      if (buildMode === 'balanced' || drafts.length <= 1) {
        setDraft({ company, ...drafts[0], confidence: sc.confidence, source: 'website' });
        setMultiDrafts(null);
        setStep('review');
      } else {
        setDraft({ company, source: 'website' }); // company carrier for the multi save
        setMultiDrafts(drafts.map((d) => ({ ...d })));
        setStep('review_multi');
      }
    } catch (err) {
      showToast(err.message || 'Could not build profiles. Please try again.', 'error');
      // Back to geography, not the start — the answer is still there, so Continue
      // is an immediate retry.
      setStep('geography');
    }
  }

  function toggleLine(key) {
    setSelectedLineKeys((keys) => {
      if (keys.includes(key)) return keys.filter((k) => k !== key);
      if (keys.length >= remainingCap) return keys; // capped by tier
      return [...keys, key];
    });
  }

  // ── Questionnaire path ─────────────────────────────────────────────────────

  const questionnaireSteps = companyExists
    ? ['q1', 'q2', 'q3', 'q4']
    : ['q_company', 'q1', 'q2', 'q3', 'q4'];

  function questionnaireProgress() {
    const idx = questionnaireSteps.indexOf(step);
    return idx === -1 ? 0 : idx + 1;
  }

  function questionnaireTotal() {
    return questionnaireSteps.length;
  }

  function nextQ() {
    const idx = questionnaireSteps.indexOf(step);
    if (idx < questionnaireSteps.length - 1) {
      setStep(questionnaireSteps[idx + 1]);
    } else {
      buildFromAnswers();
    }
  }

  function prevQ() {
    const idx = questionnaireSteps.indexOf(step);
    if (idx > 0) {
      setStep(questionnaireSteps[idx - 1]);
    } else {
      setStep('start');
    }
  }

  function validateQCompany() {
    const errors = {};
    if (!answers.company_name.trim()) errors.company_name = 'Company name is required';
    if (!answers.country) errors.country = 'Country is required';
    return errors;
  }

  async function buildFromAnswers() {
    setStep('building');
    try {
      const payload = {
        ...answers,
        ...(companyExists && existingCompany
          ? { company_name: existingCompany.company_name, country: existingCompany.country, company_size: existingCompany.company_size }
          : {}),
      };
      const result = await apiFetch('/profiles/onboarding/from-answers', {
        method: 'POST',
        body: JSON.stringify(payload),
        timeoutMs: 90000, // LLM extraction + CPV lookup
      });
      if (companyExists && existingCompany) {
        result.company = {
          company_name: existingCompany.company_name,
          country: existingCompany.country,
          company_size: existingCompany.company_size,
        };
      }
      setDraft(result);
      setStep('review');
    } catch (err) {
      showToast(err.message || 'AI mapping failed. Please try again.', 'error');
      setStep(questionnaireSteps[questionnaireSteps.length - 1]);
    }
  }

  // ── Review & save ──────────────────────────────────────────────────────────

  function updateDraftCompany(field, value) {
    setDraft((d) => ({ ...d, company: { ...d.company, [field]: value } }));
  }

  function updateDraftProfile(field, value) {
    setDraft((d) => ({ ...d, profile: { ...d.profile, [field]: value } }));
  }

  function removeFromList(field, item) {
    updateDraftProfile(field, (draft.profile[field] || []).filter((v) => v !== item));
  }

  function addToList(field, item) {
    const current = draft.profile[field] || [];
    if (item && !current.includes(item)) {
      updateDraftProfile(field, [...current, item]);
    }
  }

  // ── Multi-profile (split) review + save ──────────────────────────────────────

  function updateMultiDraft(idx, field, value) {
    setMultiDrafts((ds) => ds.map((d, i) =>
      i === idx ? { ...d, profile: { ...d.profile, [field]: value } } : d));
  }

  // Geography is one answer for the whole batch — write it to every draft at once
  // (and keep `geo` in step, so going Back shows what they actually have).
  function updateMultiGeo(next) {
    setGeo(next);
    setMultiDrafts((ds) => ds.map((d) => ({
      ...d,
      profile: {
        ...d.profile,
        operating_countries: next.countries,
        operating_regions: next.regions,
      },
    })));
  }

  function removeMultiCpv(idx, code) {
    setMultiDrafts((ds) => ds.map((d, i) =>
      i === idx
        ? { ...d, profile: { ...d.profile, cpv_interests: (d.profile.cpv_interests || []).filter((c) => c !== code) } }
        : d));
  }

  async function handleSaveMulti() {
    setSaveError('');
    if (!draft.company?.company_name?.trim() || !draft.company?.country) {
      setSaveError('Company name and country are required');
      return;
    }
    // Catch the capped-tier empty-countries case here: POST /profiles rejects it,
    // and a 403 mid-loop would leave a half-created batch.
    if (countryPickRequired && (multiDrafts[0]?.profile?.operating_countries || []).length === 0) {
      setSaveError('Pick at least one country under "Where you work" so we know which tenders to show you.');
      return;
    }
    setSaving(true);
    setStep('creating_multi');
    try {
      if (!companyExists) {
        await apiFetch('/company', {
          method: 'PUT',
          body: JSON.stringify({
            company_name: draft.company.company_name,
            country: draft.company.country,
            company_size: draft.company.company_size || null,
            experience_years: 0,
            eu_projects: false,
          }),
        });
      }
      // Non-atomic on purpose: each profile is independently valid. If one fails
      // (e.g. tier cap hit mid-loop), keep what succeeded and report.
      const createdIds = [];
      for (let i = 0; i < multiDrafts.length; i++) {
        setBuildStatus(`Creating profile ${i + 1} of ${multiDrafts.length}…`);
        try {
          const saved = await apiFetch('/profiles', {
            method: 'POST',
            body: JSON.stringify(multiDrafts[i].profile),
            timeoutMs: 120000, // HyDE + embed + synthetic generation per profile
          });
          if (saved?.id) createdIds.push(saved.id);
        } catch (err) {
          showToast(`Created ${createdIds.length} of ${multiDrafts.length} profiles — ${err.message}`, 'error');
          break;
        }
      }
      queryClient.invalidateQueries({ queryKey: QK.profiles });
      queryClient.invalidateQueries({ queryKey: QK.company });
      queryClient.invalidateQueries({ queryKey: QK.usage });
      // Seed matches for each created profile (fan-out isn't automatic).
      for (let i = 0; i < createdIds.length; i++) {
        setBuildStatus(`Finding matches (${i + 1} of ${createdIds.length})…`);
        try { await apiFetch(`/profiles/${createdIds[i]}/find-matches`, { method: 'POST', timeoutMs: 90000 }); } catch (_) { /* seeding is best-effort */ }
      }
      queryClient.invalidateQueries({ queryKey: QK.matches });
      if (createdIds.length > 0) {
        showToast(`Created ${createdIds.length} profile${createdIds.length !== 1 ? 's' : ''}.`, 'success');
        navigate('/dashboard');
      } else {
        setSaving(false);
        setStep('review_multi');
      }
    } catch (err) {
      setSaveError(err.message || 'Failed to save. Please try again.');
      setSaving(false);
      setStep('review_multi');
    }
  }

  async function handleSave() {
    setSaveError('');
    if (!draft.company.company_name?.trim()) {
      setSaveError('Company name is required');
      return;
    }
    if (!draft.company.country) {
      setSaveError('Company country is required');
      return;
    }
    // Same capped-tier rule the server enforces on create — surface it as a field
    // error rather than a 403 after the save round-trip.
    if (countryPickRequired && (draft.profile.operating_countries || []).length === 0) {
      setSaveError('Pick at least one country under "Where you work" so we know which tenders to show you.');
      return;
    }
    setSaving(true);
    try {
      if (!companyExists) {
        await apiFetch('/company', {
          method: 'PUT',
          body: JSON.stringify({
            company_name: draft.company.company_name,
            country: draft.company.country,
            company_size: draft.company.company_size || null,
            experience_years: 0,
            eu_projects: false,
          }),
        });
      }
      const saved = await apiFetch('/profiles', {
        method: 'POST',
        body: JSON.stringify(draft.profile),
        timeoutMs: 120000, // HyDE + embed + synthetic generation
      });
      queryClient.invalidateQueries({ queryKey: QK.profiles });
      queryClient.invalidateQueries({ queryKey: QK.company });
      queryClient.invalidateQueries({ queryKey: QK.usage });
      queryClient.invalidateQueries({ queryKey: QK.matches });
      const profileId = saved?.id;
      if (profileId) {
        setSavedProfileId(profileId);
        setSavedProfileName(draft.profile.profile_name || 'Your profile');
        setMode('created');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setSaveError(err.message || 'Failed to save. Please try again.');
      setSaving(false);
    }
  }

  async function handleFindMatchesFromCreate() {
    const profileId = savedProfileId;
    setMode('finding');
    setMatchProgress(0);
    const startTime = Date.now();
    const timer = setInterval(() => {
      setMatchProgress(Math.min(80, ((Date.now() - startTime) / 4000) * 80));
    }, 100);
    try {
      const result = await apiFetch(`/profiles/${profileId}/find-matches`, { method: 'POST', timeoutMs: 90000 });
      clearInterval(timer);
      setMatchProgress(100);
      queryClient.invalidateQueries({ queryKey: QK.matches });
      const count = result?.matches_found ?? 0;
      showToast(`Found ${count} potential match${count !== 1 ? 'es' : ''}.`, 'success');
      setTimeout(() => navigate(`/dashboard?profile_id=${profileId}`), 400);
    } catch (err) {
      clearInterval(timer);
      setMode('created');
      setMatchProgress(0);
      showToast('Failed to find matches: ' + err.message, 'error');
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (mode === 'created' || mode === 'finding') {
    return (
      <div style={{ maxWidth: 520, margin: '72px auto', padding: '0 24px' }}>
        <div style={{
          background: '#fff',
          borderRadius: 14,
          border: '2px solid #0A1F4A',
          padding: '52px 48px',
          textAlign: 'center',
          boxShadow: '0 6px 0 0 #0A1F4A',
        }}>
          <div style={{ fontSize: 38, marginBottom: 16, color: '#16a34a' }}>✓</div>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: '#0A1F4A', marginBottom: 10 }}>
            Profile created!
          </h1>
          <p style={{ fontSize: 15, color: '#64748b', marginBottom: 36, lineHeight: 1.6 }}>
            <strong style={{ color: '#0A1F4A' }}>{savedProfileName}</strong> is ready.{' '}
            Let's find your matching EU tenders.
          </p>

          {mode === 'finding' ? (
            <div>
              <div style={{ background: '#ECEEF3', borderRadius: 8, height: 10, marginBottom: 10, overflow: 'hidden' }}>
                <div style={{
                  background: '#FF7A3D',
                  width: `${matchProgress}%`,
                  height: '100%',
                  borderRadius: 8,
                  transition: 'width 0.2s ease',
                }} />
              </div>
              <p style={{ fontSize: 13, color: '#94a3b8', marginTop: 6 }}>Searching for matching tenders…</p>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={handleFindMatchesFromCreate}
                style={{
                  background: '#FF7A3D',
                  color: '#fff',
                  border: 'none',
                  fontWeight: 700,
                  fontSize: 16,
                  padding: '14px 0',
                  borderRadius: 8,
                  cursor: 'pointer',
                  boxShadow: '0 4px 0 0 #0A1F4A',
                  width: '100%',
                  maxWidth: 280,
                  display: 'block',
                  margin: '0 auto',
                }}
              >
                Find matches →
              </button>
              <div style={{ marginTop: 20 }}>
                <button
                  type="button"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#94a3b8', textDecoration: 'underline' }}
                  onClick={() => navigate(`/profiles/${savedProfileId}`)}
                >
                  Edit profile
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (companyExists === null) {
    return (
      <div style={s.page}>
        <Spinner label="Loading…" />
      </div>
    );
  }

  // Start screen
  if (step === 'start') {
    return (
      <div style={s.page}>
        <h1 style={s.heading}>
          {source === 'profile' ? 'Create a new search profile' : "Let's set up your profile"}
        </h1>
        <p style={s.subheading}>
          {source === 'profile'
            ? 'Choose how you want to define this new search profile.'
            : 'Jyto needs to understand what you do so it can find the right tenders. Pick the fastest option for you.'}
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 32 }}>
          <div style={s.pathCard(false)} onClick={() => setStep('scan_url')}>
            <div style={s.pathIcon}>🌐</div>
            <div style={s.pathTitle}>Scan my website</div>
            <div style={s.pathDesc}>We analyze your site and build a draft profile automatically. Fastest option.</div>
          </div>
          <div style={s.pathCard(false)} onClick={() => setStep(questionnaireSteps[0])}>
            <div style={s.pathIcon}>💬</div>
            <div style={s.pathTitle}>Quick questions</div>
            <div style={s.pathDesc}>Answer 4–5 short questions and we map your answers to the right categories.</div>
          </div>
          <div style={s.pathCard(false)} onClick={() => navigate(source === 'profile' ? '/profiles/new' : '/profile')}>
            <div style={s.pathIcon}>✏️</div>
            <div style={s.pathTitle}>Fill in manually</div>
            <div style={s.pathDesc}>Go straight to the full form if you prefer to fill everything yourself.</div>
          </div>
        </div>
      </div>
    );
  }

  // Website URL input
  if (step === 'scan_url') {
    return (
      <div style={s.page}>
        <button style={s.btnGhost} onClick={() => setStep('start')}>← Back</button>
        <h1 style={{ ...s.heading, marginTop: 16 }}>Enter your company website</h1>
        <p style={s.subheading}>We'll read your homepage and services pages to work out what you do and the right procurement categories.</p>

        <div style={s.fieldWrap}>
          <label style={s.fieldLabel}>Website URL</label>
          <input
            style={scanError ? s.urlInputError : s.urlInput}
            value={scanUrl}
            onChange={(e) => { setScanUrl(e.target.value); setScanError(''); }}
            placeholder="https://www.yourcompany.com"
            onKeyDown={(e) => e.key === 'Enter' && handleScan()}
            autoFocus
          />
          {scanError && <div style={s.fieldError}>{scanError}</div>}
          <div style={s.fieldHint}>Works best on public marketing pages — not login-protected intranets.</div>
        </div>

        <div style={s.btnRow}>
          <button style={s.btnPrimary(false)} onClick={handleScan}>Analyze website →</button>
          <button style={s.btnGhost} onClick={() => setStep(questionnaireSteps[0])}>
            Skip — use questionnaire instead
          </button>
        </div>
      </div>
    );
  }

  // Scanning spinner
  if (step === 'scanning') {
    return (
      <div style={s.page}>
        <Spinner label="Analyzing your website — reading your services pages. This can take up to 20 seconds." />
      </div>
    );
  }

  // Choose which product lines to build (only shown when the split is obvious)
  if (step === 'choose_lines' && scan) {
    const lines = scan.product_lines || [];
    const capText = remainingCap === Infinity ? 'unlimited' : remainingCap;
    const overCap = remainingCap !== Infinity && lines.length > remainingCap;
    return (
      <div style={s.page}>
        <button style={s.btnGhost} onClick={() => setStep('scan_url')}>← Back</button>
        <h1 style={{ ...s.heading, marginTop: 16 }}>We found {lines.length} distinct service lines</h1>
        <p style={s.subheading}>
          Focused profiles match far better than one broad one. Pick the lines you want us to
          build — each becomes its own search profile — or build a single combined profile
          covering everything.
        </p>
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
          Your {planLabel} plan includes {capText} profile{remainingCap === 1 ? '' : 's'}.
        </div>

        <div style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
          {lines.map((l) => {
            const selected = selectedLineKeys.includes(l.key);
            const locked = !selected && selectedLineKeys.length >= remainingCap;
            return (
              <div
                key={l.key}
                onClick={() => !locked && toggleLine(l.key)}
                style={{
                  border: selected ? '2px solid #1e40af' : '1px solid #e2e8f0',
                  background: selected ? '#eff6ff' : locked ? '#f8fafc' : '#fff',
                  borderRadius: 12, padding: '16px 18px',
                  cursor: locked ? 'not-allowed' : 'pointer',
                  opacity: locked ? 0.55 : 1,
                  display: 'flex', gap: 12, alignItems: 'flex-start',
                }}
              >
                <input type="checkbox" checked={selected} readOnly disabled={locked} style={{ marginTop: 3 }} />
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>{l.label}</div>
                  {l.one_liner && <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{l.one_liner}</div>}
                  {l.market && (
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {l.market}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {overCap && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 8, padding: '12px 16px', fontSize: 14, marginBottom: 20 }}>
            Your plan fits {remainingCap} of these {lines.length} lines.{' '}
            <button
              style={{ background: 'none', border: 'none', padding: 0, color: '#92400e', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => navigate('/billing')}
            >
              Upgrade
            </button>{' '}
            to give every line its own focused profile.
          </div>
        )}

        <div style={s.btnRow}>
          <button
            style={s.btnPrimary(selectedLineKeys.length === 0)}
            disabled={selectedLineKeys.length === 0}
            onClick={() => goToGeography('split')}
          >
            Continue with {selectedLineKeys.length} profile{selectedLineKeys.length !== 1 ? 's' : ''} →
          </button>
          <button style={s.btnSecondary} onClick={() => goToGeography('balanced')}>
            Or use one combined profile
          </button>
        </div>
      </div>
    );
  }

  // ── Geography — one answer, applied to every profile in the batch ───────────
  if (step === 'geography' && pendingBuild) {
    const nProfiles = pendingBuild.mode === 'split' ? selectedLineKeys.length : 1;
    const cameFromLines = (pendingBuild.scan?.product_lines || []).length >= 2 && pendingBuild.scan?.multi;
    // Capped tiers must pick — the backend rejects an empty list at create time,
    // so stop them here with copy rather than with a 403 after the build is paid for.
    const blocked = countryPickRequired && geo.countries.length === 0;

    return (
      <div style={s.page}>
        <button style={s.btnGhost} onClick={() => setStep(cameFromLines ? 'choose_lines' : 'scan_url')}>← Back</button>
        <h1 style={{ ...s.heading, marginTop: 16 }}>Where do you deliver?</h1>
        <p style={s.subheading}>
          We'll only show you tenders you can actually bid on
          {nProfiles > 1 ? `, across all ${nProfiles} profiles we're about to create` : ''}.{' '}
          <strong style={{ fontWeight: 600, color: '#475569' }}>You can fine-tune it per profile later.</strong>
        </p>

        <div style={s.questionLabel}>Countries</div>
        <div style={s.questionHint}>
          {countryPickRequired
            ? `Your ${planLabel} plan covers ${countriesMax} ${countriesMax === 1 ? 'country' : 'countries'}.`
            : 'Pick every country you deliver in, or leave empty for all EU countries.'}
        </div>

        <GeographyPicker
          value={geo}
          onChange={setGeo}
          max={countriesMax}
          onUpgrade={() => navigate('/billing')}
        />

        <div style={s.btnRow}>
          <button
            style={s.btnPrimary(blocked)}
            disabled={blocked}
            onClick={() => buildProfiles(pendingBuild.mode, pendingBuild.scan)}
          >
            {blocked
              ? 'Pick a country to continue'
              : `Build ${nProfiles === 1 ? 'my profile' : `my ${nProfiles} profiles`} →`}
          </button>
          {!countryPickRequired && geo.countries.length > 0 && (
            <button style={s.btnSecondary} onClick={() => setGeo({ countries: [], regions: [] })}>
              Clear — search all EU countries
            </button>
          )}
        </div>
      </div>
    );
  }

  // Building the profile draft(s) — from answers or from the website selection
  if (step === 'building') {
    return (
      <div style={s.page}>
        <Spinner label="Preparing your profiles for review… this takes a few seconds." />
      </div>
    );
  }

  // Creating + seeding multiple profiles (split path) — shows per-step progress
  if (step === 'creating_multi') {
    return (
      <div style={s.page}>
        <Spinner label={buildStatus || 'Creating your profiles…'} />
      </div>
    );
  }

  // ── Questionnaire steps ────────────────────────────────────────────────────

  const qProgress = questionnaireProgress();
  const qTotal = questionnaireTotal();

  if (step === 'q_company') {
    return (
      <div style={s.page}>
        <button style={s.btnGhost} onClick={() => setStep('start')}>← Back</button>
        <ProgressBar current={qProgress} total={qTotal} />
        <div style={s.questionLabel}>Tell us about your company</div>
        <div style={s.questionHint}>This links your search profiles to the right country and sector.</div>

        <div style={s.fieldWrap}>
          <label style={s.fieldLabel}>Company name *</label>
          <input
            style={fieldErrors.company_name ? s.inputError : s.input}
            value={answers.company_name}
            onChange={(e) => setAnswer('company_name', e.target.value)}
            placeholder="e.g. Acme Software BV"
            autoFocus
          />
          {fieldErrors.company_name && <div style={s.fieldError}>{fieldErrors.company_name}</div>}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={s.fieldWrap}>
            <label style={s.fieldLabel}>Country *</label>
            <select
              style={fieldErrors.country ? s.inputError : s.select}
              value={answers.country}
              onChange={(e) => setAnswer('country', e.target.value)}
            >
              <option value="">Select country…</option>
              {COUNTRY_ISO.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
            {fieldErrors.country && <div style={s.fieldError}>{fieldErrors.country}</div>}
          </div>
          <div style={s.fieldWrap}>
            <label style={s.fieldLabel}>Company size</label>
            <select
              style={s.select}
              value={answers.company_size}
              onChange={(e) => setAnswer('company_size', e.target.value)}
            >
              <option value="">Select size…</option>
              {SIZES.map((sz) => <option key={sz} value={sz}>{sz} employees</option>)}
            </select>
          </div>
        </div>

        <div style={s.btnRow}>
          <button
            style={s.btnPrimary(false)}
            onClick={() => {
              const errors = validateQCompany();
              if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }
              nextQ();
            }}
          >
            Continue →
          </button>
        </div>
      </div>
    );
  }

  if (step === 'q1') {
    return (
      <div style={s.page}>
        <button style={s.btnGhost} onClick={prevQ}>← Back</button>
        <ProgressBar current={qProgress} total={qTotal} />
        <div style={s.questionLabel}>What does your company primarily deliver?</div>
        <div style={s.questionHint}>Pick the one that fits best — you can refine later.</div>

        <div style={s.optionGrid}>
          {BUSINESS_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              style={s.optionChip(answers.business_type === type)}
              onClick={() => { setAnswer('business_type', type); setAnswer('services', []); }}
            >
              {type}
            </button>
          ))}
        </div>

        <div style={s.btnRow}>
          <button
            style={s.btnPrimary(!answers.business_type)}
            disabled={!answers.business_type}
            onClick={nextQ}
          >
            Continue →
          </button>
        </div>
      </div>
    );
  }

  if (step === 'q2') {
    const serviceOptions = SERVICES_BY_TYPE[answers.business_type] || SERVICES_BY_TYPE['Other'];
    return (
      <div style={s.page}>
        <button style={s.btnGhost} onClick={prevQ}>← Back</button>
        <ProgressBar current={qProgress} total={qTotal} />
        <div style={s.questionLabel}>Which services do you offer?</div>
        <div style={s.questionHint}>Select all that apply — these map to the right tender categories.</div>

        <div style={s.optionGrid}>
          {serviceOptions.map((svc) => {
            const selected = answers.services.includes(svc);
            return (
              <button
                key={svc}
                type="button"
                style={s.optionChip(selected)}
                onClick={() => setAnswer('services', selected
                  ? answers.services.filter((s) => s !== svc)
                  : [...answers.services, svc]
                )}
              >
                {svc}
              </button>
            );
          })}
        </div>

        <div style={s.btnRow}>
          <button style={s.btnPrimary(false)} onClick={nextQ}>
            {answers.services.length === 0 ? 'Skip →' : 'Continue →'}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'q3') {
    return (
      <div style={s.page}>
        <button style={s.btnGhost} onClick={prevQ}>← Back</button>
        <ProgressBar current={qProgress} total={qTotal} />
        <div style={s.questionLabel}>Where do you deliver?</div>
        <div style={s.questionHint}>
          {countryPickRequired
            ? `We'll only show you tenders you can actually bid on. Your ${planLabel} plan covers ${countriesMax} ${countriesMax === 1 ? 'country' : 'countries'} — you can fine-tune it per profile later.`
            : "Select the countries where you actively deliver work. Leave empty for all EU countries — you can fine-tune it per profile later."}
        </div>

        <GeographyPicker
          value={{ countries: answers.countries, regions: answers.regions }}
          onChange={(next) => setAnswers((a) => ({ ...a, countries: next.countries, regions: next.regions }))}
          max={countriesMax}
          onUpgrade={() => navigate('/billing')}
        />

        <div style={s.btnRow}>
          {/* No "skip" on a capped tier — POST /profiles rejects an empty list at
              create time, so skipping here would only fail at the last step. */}
          <button
            style={s.btnPrimary(countryPickRequired && answers.countries.length === 0)}
            disabled={countryPickRequired && answers.countries.length === 0}
            onClick={nextQ}
          >
            {countryPickRequired && answers.countries.length === 0
              ? 'Pick a country to continue'
              : answers.countries.length === 0 ? 'Skip — all countries →' : 'Continue →'}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'q4') {
    return (
      <div style={s.page}>
        <button style={s.btnGhost} onClick={prevQ}>← Back</button>
        <ProgressBar current={qProgress} total={qTotal} />
        <div style={s.questionLabel}>Describe what your company does and who you serve</div>
        <div style={s.questionHint}>
          This is the most important field for match quality. Be specific about the work you
          deliver and the buyers you serve — not the tenders you're searching for.
        </div>

        <textarea
          style={s.textarea}
          value={answers.description}
          onChange={(e) => setAnswer('description', e.target.value)}
          placeholder="e.g. We provide custom data management software and system integration services to national health agencies and local government bodies across the Netherlands and Belgium. We specialise in GDPR-compliant data pipelines and interoperability between legacy systems."
          autoFocus
        />
        <div style={s.fieldHint}>The more specific, the better your matches will be.</div>

        <div style={s.btnRow}>
          <button
            style={s.btnPrimary(false)}
            onClick={nextQ}
          >
            {answers.description.trim() ? 'Build my profile →' : 'Skip & build profile →'}
          </button>
        </div>
      </div>
    );
  }

  // ── Multi-profile review (split path) — compact per-profile cards ───────────

  if (step === 'review_multi' && multiDrafts) {
    return (
      <div style={s.page}>
        <h1 style={s.heading}>Review your {multiDrafts.length} profiles</h1>
        <p style={s.subheading}>
          We drafted a focused profile for each line you picked. Adjust the essentials here —
          you can refine every detail later from each profile.
        </p>

        {saveError && <div style={s.errorBox}>{saveError}</div>}

        {!companyExists && (
          <div style={s.reviewSection}>
            <div style={s.reviewSectionTitle}>Your company</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={s.fieldWrap}>
                <label style={s.fieldLabel}>Company name *</label>
                <input
                  style={s.input}
                  value={draft.company.company_name}
                  onChange={(e) => updateDraftCompany('company_name', e.target.value)}
                  placeholder="Company name"
                />
              </div>
              <div style={s.fieldWrap}>
                <label style={s.fieldLabel}>Country *</label>
                <select
                  style={s.select}
                  value={draft.company.country}
                  onChange={(e) => updateDraftCompany('country', e.target.value)}
                >
                  <option value="">Select country…</option>
                  {COUNTRY_ISO.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Geography — ONE section for the batch, not one per card. It's a
            company-level fact, and per-profile divergence is a refinement for
            someone who has already seen their matches. */}
        <div style={s.reviewSection}>
          <div style={s.reviewSectionTitle}>Where you work</div>
          <div style={{ ...s.questionHint, marginBottom: 16 }}>
            Applies to all {multiDrafts.length} profile{multiDrafts.length !== 1 ? 's' : ''} —
            you can fine-tune it per profile later.
          </div>
          <GeographyPicker
            value={geo}
            onChange={updateMultiGeo}
            max={countriesMax}
            onUpgrade={() => navigate('/billing')}
          />
        </div>

        {multiDrafts.map((d, idx) => {
          const cpvLabels = { ...CPV_LABELS, ...(d.cpv_labels || {}) };
          return (
            <div key={idx} style={s.reviewSection}>
              <div style={s.reviewSectionTitle}>Profile {idx + 1}</div>
              <div style={s.fieldWrap}>
                <label style={s.fieldLabel}>Profile name</label>
                <input
                  style={s.input}
                  value={d.profile.profile_name}
                  onChange={(e) => updateMultiDraft(idx, 'profile_name', e.target.value)}
                />
              </div>
              <div style={s.fieldWrap}>
                <label style={s.fieldLabel}>What you do (capabilities)</label>
                <textarea
                  style={s.textarea}
                  value={d.profile.capabilities_description || ''}
                  onChange={(e) => updateMultiDraft(idx, 'capabilities_description', e.target.value)}
                />
              </div>
              <div>
                <label style={s.fieldLabel}>Procurement categories</label>
                <div style={{ marginTop: 6 }}>
                  <ChipList
                    items={d.profile.cpv_interests}
                    labels={cpvLabels}
                    onRemove={(code) => removeMultiCpv(idx, code)}
                  />
                </div>
              </div>
            </div>
          );
        })}

        <div style={{ fontSize: 13, color: '#64748b', marginTop: 8 }}>
          Last step — this creates {multiDrafts.length === 1 ? 'this profile' : `these ${multiDrafts.length} profiles`} and finds your matches.
        </div>
        <div style={s.btnRow}>
          <button style={s.btnPrimary(saving)} onClick={handleSaveMulti} disabled={saving}>
            {saving ? 'Saving…' : `Create ${multiDrafts.length} profile${multiDrafts.length !== 1 ? 's' : ''} & find matches →`}
          </button>
          <button style={s.btnSecondary} onClick={() => setStep('geography')} disabled={saving}>
            Back
          </button>
        </div>
      </div>
    );
  }

  // ── Review & Confirm ───────────────────────────────────────────────────────

  if (step === 'review' && draft) {
    // Curated tree labels + full-table labels for the suggested 8-digit codes
    // (the backend sends cpv_labels alongside the draft).
    const cpvLabels = { ...CPV_LABELS, ...(draft.cpv_labels || {}) };
    const confText = draft.confidence >= 0.7 ? 'Good match data found'
      : draft.confidence >= 0.4 ? 'Partial match data — review carefully'
      : 'Limited data — please review and fill gaps';
    const reviewAtCountryCap =
      countryPickRequired && (draft.profile.operating_countries || []).length >= countriesMax;

    return (
      <div style={s.page}>
        <h1 style={s.heading}>Review your profile</h1>
        <p style={s.subheading}>
          {draft.source === 'website'
            ? 'Here is what we found on your website. Remove anything that does not fit and fill in any gaps.'
            : 'Here is what we built from your answers. Adjust anything before saving.'}
        </p>

        <div style={s.confidenceBadge(draft.confidence)}>{confText}</div>

        {saveError && <div style={s.errorBox}>{saveError}</div>}

        {/* Company section — only editable if company doesn't exist yet */}
        {!companyExists && (
          <div style={s.reviewSection}>
            <div style={s.reviewSectionTitle}>Your company</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={s.fieldWrap}>
                <label style={s.fieldLabel}>Company name *</label>
                <input
                  style={s.input}
                  value={draft.company.company_name}
                  onChange={(e) => updateDraftCompany('company_name', e.target.value)}
                  placeholder="Company name"
                />
              </div>
              <div style={s.fieldWrap}>
                <label style={s.fieldLabel}>Country *</label>
                <select
                  style={s.select}
                  value={draft.company.country}
                  onChange={(e) => updateDraftCompany('country', e.target.value)}
                >
                  <option value="">Select country…</option>
                  {COUNTRY_ISO.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div style={s.fieldWrap}>
                <label style={s.fieldLabel}>Company size</label>
                <select
                  style={s.select}
                  value={draft.company.company_size || ''}
                  onChange={(e) => updateDraftCompany('company_size', e.target.value)}
                >
                  <option value="">Select size…</option>
                  {SIZES.map((sz) => <option key={sz} value={sz}>{sz} employees</option>)}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Profile name (no sector — removed 2026-07-19; CPV categories below
            carry the domain in official vocabulary) */}
        <div style={s.reviewSection}>
          <div style={s.reviewSectionTitle}>Profile name</div>
          <input
            style={s.input}
            value={draft.profile.profile_name}
            onChange={(e) => updateDraftProfile('profile_name', e.target.value)}
            placeholder="e.g. IT Services Netherlands"
          />
          <div style={s.fieldHint}>A short name to identify this search profile</div>
        </div>

        {/* Specialisations */}
        <div style={s.reviewSection}>
          <div style={s.reviewSectionTitle}>What you offer</div>
          <ChipList
            items={draft.profile.specialisations}
            onRemove={(item) => removeFromList('specialisations', item)}
          />
          <div style={{ marginTop: 10 }}>
            <AddChipInput
              placeholder="+ Add specialisation"
              onAdd={(item) => addToList('specialisations', item)}
            />
          </div>
          <div style={s.fieldHint}>Short phrases describing your specific expertise (2–5 words each)</div>
        </div>

        {/* Capabilities description — the richest matching signal */}
        <div style={s.reviewSection}>
          <div style={s.reviewSectionTitle}>What you do (capabilities)</div>
          <textarea
            style={s.textarea}
            value={draft.profile.capabilities_description || ''}
            onChange={(e) => updateDraftProfile('capabilities_description', e.target.value)}
            placeholder="The concrete products, services and technologies your company delivers…"
          />
          <div style={s.fieldHint}>
            This is the strongest driver of match quality. We drafted it from your{' '}
            {draft.source === 'website' ? 'website' : 'answers'} — correct anything inaccurate and
            add real specifics (products, technologies, sectors you serve).
          </div>
        </div>

        {/* Reference projects — past deliveries */}
        <div style={s.reviewSection}>
          <div style={s.reviewSectionTitle}>Past projects &amp; clients</div>
          <textarea
            style={s.textarea}
            value={draft.profile.reference_projects || ''}
            onChange={(e) => updateDraftProfile('reference_projects', e.target.value)}
            placeholder="2–3 concrete things you've delivered — what, for whom, in what sector. One per line."
          />
          <div style={s.fieldHint}>
            What you've already delivered is the best predictor of what you can win. Add real
            examples — we only kept ones explicitly on your {draft.source === 'website' ? 'website' : 'answers'}.
          </div>
        </div>

        {/* Contract-size capacity */}
        <div style={s.reviewSection}>
          <div style={s.reviewSectionTitle}>Contract size you can handle</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={s.fieldWrap}>
              <label style={s.fieldLabel}>Smallest</label>
              <select
                style={s.select}
                value={draft.profile.contract_size_min || ''}
                onChange={(e) => updateDraftProfile('contract_size_min', e.target.value)}
              >
                {CONTRACT_SIZE_OPTIONS.map((o) => <option key={o.value || 'any'} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div style={s.fieldWrap}>
              <label style={s.fieldLabel}>Largest</label>
              <select
                style={s.select}
                value={draft.profile.contract_size_max || ''}
                onChange={(e) => updateDraftProfile('contract_size_max', e.target.value)}
              >
                {CONTRACT_SIZE_OPTIONS.map((o) => <option key={o.value || 'any'} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div style={s.fieldHint}>Optional. Helps us skip contracts far outside what you can realistically deliver.</div>
        </div>

        {/* CPV categories */}
        <div style={s.reviewSection}>
          <div style={s.reviewSectionTitle}>Procurement categories</div>
          {draft.profile.cpv_interests.length === 0 ? (
            <div style={{ ...s.errorBox, marginBottom: 0, fontSize: 13 }}>
              No categories selected — without these, all tenders are included in the filter. Consider adding at least one.
            </div>
          ) : (
            <ChipList
              items={draft.profile.cpv_interests}
              labels={cpvLabels}
              onRemove={(item) => removeFromList('cpv_interests', item)}
            />
          )}
          <div style={s.fieldHint}>These are used to filter tenders. Remove any that don't apply to you.</div>
        </div>

        {/* Operating countries */}
        <div style={s.reviewSection}>
          <div style={s.reviewSectionTitle}>Where you work</div>
          {draft.profile.operating_countries.length === 0 ? (
            <span style={s.emptyHint}>
              {countryPickRequired
                ? 'Pick the country you deliver in — we need it to show you tenders.'
                : 'Searching all EU countries'}
            </span>
          ) : (
            <ChipList
              items={draft.profile.operating_countries}
              labels={COUNTRY_CODE_TO_NAME}
              gray
              onRemove={(item) => removeFromList('operating_countries', item)}
            />
          )}
          {/* Same cap the geography step enforces — without this, the limit is
              bypassable one screen later and only surfaces as a 403 on save. */}
          {reviewAtCountryCap ? (
            <div style={s.capNotice}>
              Your plan covers {countriesMax} {countriesMax === 1 ? 'country' : 'countries'}.{' '}
              <button style={s.capUpgrade} onClick={() => navigate('/billing')}>Upgrade</button>{' '}
              to find tenders across the EU.
            </div>
          ) : (
            <div style={{ marginTop: 10 }}>
              <select
                style={{ ...s.select, width: 'auto', fontSize: 13 }}
                value=""
                onChange={(e) => { if (e.target.value) addToList('operating_countries', e.target.value); }}
              >
                <option value="">+ Add country</option>
                {COUNTRY_ISO.filter((c) => !(draft.profile.operating_countries || []).includes(c.code)).map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
          )}
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>
              Delivery regions <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span>
            </div>
            <RegionSelect
              operatingCountries={draft.profile.operating_countries}
              value={draft.profile.operating_regions || []}
              onChange={(v) => updateDraftProfile('operating_regions', v)}
            />
          </div>
          <div style={s.laterHint}>You can change this any time from the profile.</div>
        </div>

        {/* Contract types */}
        <div style={s.reviewSection}>
          <div style={s.reviewSectionTitle}>Contract types</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 24px' }}>
            {PROCUREMENT_TYPE_OPTIONS.map((opt) => {
              const checked = (draft.profile.procurement_types || []).includes(opt);
              return (
                <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer', color: '#374151' }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => updateDraftProfile(
                      'procurement_types',
                      checked
                        ? draft.profile.procurement_types.filter((t) => t !== opt)
                        : [...(draft.profile.procurement_types || []), opt]
                    )}
                  />
                  {opt}
                </label>
              );
            })}
          </div>
        </div>

        {/* Keywords — short supply-register summary */}
        <div style={s.reviewSection}>
          <div style={s.reviewSectionTitle}>Summary — what you deliver &amp; to whom</div>
          <textarea
            style={s.textarea}
            value={draft.profile.keywords}
            onChange={(e) => updateDraftProfile('keywords', e.target.value)}
            placeholder="In 2–3 sentences: the work you deliver and the public-sector buyers you serve. e.g. 'We deliver GDPR-compliant data platforms and system integration for national health agencies and municipalities.'"
          />
          <div style={s.fieldHint}>
            Describe what you <strong>deliver</strong> — not the tenders you're searching for.
            This is embedded and compared against tender descriptions, so supply-side wording
            matches best.
          </div>
        </div>

        <div style={s.btnRow}>
          <button style={s.btnPrimary(saving)} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save profile →'}
          </button>
          <button style={s.btnSecondary} onClick={() => setStep('start')} disabled={saving}>
            Start over
          </button>
        </div>
      </div>
    );
  }

  return null;
}
